import { getImageUrl } from '../utils/imageUrls';
import { Upload } from 'lucide-react';
import { useDispatch } from 'react-redux';
import { setHelpMenu } from '../Redux/store';
import ShieldCamera from './ShieldCamera';

/**
 * ImageDisplay - Displays an image from image data JSON
 *
 * Purpose: Takes a JSON object containing UUID, userId, and bucket, then displays
 * the image by constructing the storage path {userId}/{UUID} and converting to URL.
 *
 * Also shows the upload type indicator
 * and loading state (as determined by loading json attribute) with a spinner
 *
 * BACKWARD COMPATIBILITY: Handles both old string URLs and new JSONB objects
 * - Old format: string URL (e.g., "https://...")
 * - New format: JSONB object with structure below
 *
 * JSON Structure Expected (new format):
 * {
 *   imageUUID: string,  // The UUID of the image file in storage (just the UUID, not the full path)
 *   userId: string,     // The user ID who uploaded the image (for security and cleanup)
 *   bucket: string,     // The storage bucket name (e.g., "chat-images", "profile-images", "ovulation-tests")
 *   source: string,      // How the image was captured - "camera" or "upload"
 *   previewUrl: string,  // Optional local data URL for preview during upload (not stored in database)
 *   loading: boolean     // Optional boolean for UI loading state during upload (not stored in database)
 * }
 *
 * @param {Object|string} imageData - Image data object with all metadata, or string URL (old format)
 * @param {string} imageData.imageUUID - The UUID of the image in storage (new format)
 * @param {string} imageData.userId - The user ID who uploaded the image (new format)
 * @param {string} imageData.bucket - Storage bucket name (new format)
 * @param {string} imageData.source - Source type: 'camera' or 'upload' (new format)
 * @param {string} imageData.previewUrl - Optional local preview URL for uploads in progress (new format)
 * @param {boolean} imageData.loading - Whether the image is currently uploading (new format)
 * @param {string} className - Additional CSS classes for the img element
 * @param {string} imageStrategy - CSS object-fit strategy: 'cover' (default) or 'contain'
 * @param {boolean} showIcons - Whether to show source icons (camera/upload). Default: true
 * @param {boolean} showLoading - Whether to show loading spinner. Default: true
 */
function ImageDisplay({ imageData, className = '', imageStrategy = 'cover', showIcons = true, showLoading = true, style, iconStyle = 'bottom-right', onClick }) {
  const dispatch = useDispatch();

  if (!imageData) {
    return null;
  }

  // Handle backward compatibility: check if imageData is a string (old format) or object (new format)
  // If it's a string that looks like JSON (starts with '{'), parse it as JSONB object
  let isOldFormat = typeof imageData === 'string';
  let data = isOldFormat ? null : imageData;

  // Check if string is actually a JSONB object (database returns JSONB as string when column is text[])
  if (isOldFormat && typeof imageData === 'string' && imageData.trim().startsWith('{')) {
    try {
      data = JSON.parse(imageData);
      isOldFormat = false; // It's actually new format, just stringified
    } catch (e) {
      // Keep as old format (URL)
    }
  }

  const { imageUUID, userId, bucket, previewUrl, source, loading } = data || {};

  // Use previewUrl if available (for local preview during upload), otherwise construct from storage
  // For old format (string URL), use it directly
  // For new format (JSONB object), construct URL from UUID, userId, and bucket
  // Fallback: if no imageUUID (upload in progress), use previewUrl
  let src = previewUrl || (isOldFormat ? imageData : (imageUUID && getImageUrl(`${userId}/${imageUUID}`, bucket)));

  // Additional fallback: if src is still null/undefined, try to handle edge cases
  if (!src && data) {
    // Handle case where imageUUID might be nested or data structure is unexpected
    if (typeof data === 'object' && data !== null) {
      // Try to extract UUID from various possible structures
      const possibleUUID = data.imageUUID || data.uuid || data.id || data.image_uuid;
      const possibleUserId = data.userId || data.user_id || data.userid;
      const possibleBucket = data.bucket || data.storage;

      if (possibleUUID && possibleUserId && possibleBucket) {
        src = getImageUrl(`${possibleUserId}/${possibleUUID}`, possibleBucket);
      }
    }
  }

  // If still no valid src, return a placeholder or null
  if (!src) {
    return null;
  }

  const handleShieldClick = () => {
    dispatch(setHelpMenu('image_shield'));
  };

  return (
    <div className="relative h-full" style={style} onClick={onClick}>
      <img
        src={src}
        alt=""
        className={className}
        style={{ objectFit: imageStrategy }}
        onError={(e) => {
          console.error('[ImageDisplay] Image failed to load:', src, e);
          // Optionally set a fallback or handle error
        }}
      />
      {showLoading && loading && (
        <div className="absolute bottom-2 left-2">
          <div className="h-4 w-4 animate-spin rounded-full border-2 border-primary border-t-transparent" />
        </div>
      )}
      {showIcons && source && (
        <div className={`absolute z-10 ${iconStyle === 'top-left' ? 'top-4 left-4' : 'bottom-1 right-1'}`} onClick={(e) => e.stopPropagation()}>
          {source === 'camera' ? (
            <div className="cursor-pointer" onClick={handleShieldClick}>
              <ShieldCamera color="text-purple-500" />
            </div>
          ) : (
            <Upload size={16} className="text-white drop-shadow-md" />
          )}
        </div>
      )}
    </div>
  );
}

export default ImageDisplay;
